import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, Package, RefreshCw, ShieldCheck, ShoppingBag, ShoppingCart, Sparkles, Truck } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductTile, { money } from '../components/shop/ProductTile';
import { setSeoMetadata } from '../lib/seo';
import { useCart } from '../context/CartContext';
import { useSiteFeatureFlags } from '../hooks/useSiteFeatureFlags';
import { useShopCategories } from '../hooks/useShopCategories';
import ShopCategoryMenu from '../components/shop/ShopCategoryMenu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Gift shop home - the store's own landing page.
 *
 * Deliberately NOT a category grid: the categories live in the GIFT SHOP menu dropdown.
 * This page sells: a hero, the items the owner has starred as Featured in the admin, new
 * arrivals, best sellers, a live cart summary and the shipping/returns reassurance strip.
 */
const Row = ({ title, subtitle, products, onAddToCart, testId, cta }) => {
  if (!products.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 py-10" data-testid={testId}>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold uppercase italic tracking-tight text-slate-900 sm:text-3xl">{title}</h2>
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        </div>
        {cta && (
          <Link to={cta.href} className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-blue-800 hover:text-blue-900">
            {cta.label} <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {products.map((p) => <ProductTile key={p.id} product={p} onAddToCart={onAddToCart} />)}
      </div>
    </section>
  );
};

const ShopLandingPage = () => {
  const { cart_enabled, left_menu_enabled, _loaded } = useSiteFeatureFlags();
  const { categories } = useShopCategories();
  const { addToCart, getCartCount, getCartTotal } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSeoMetadata({
      title: 'Gift Shop',
      description: 'St John gift shop - tees, hats, visors, stickers and island gear, shipped from St John, U.S. Virgin Islands.',
      canonicalPath: '/shop',
      ogType: 'website',
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/store/products`, { params: { limit: 200 } })
      .then((res) => { if (!cancelled) setProducts(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setProducts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const featured = useMemo(() => products.filter((p) => p.featured).slice(0, 8), [products]);
  const newArrivals = useMemo(
    () => [...products].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''))).slice(0, 8),
    [products],
  );
  const bestSellers = useMemo(
    () => [...products].filter((p) => (p.sold_count || 0) > 0).sort((a, b) => (b.sold_count || 0) - (a.sold_count || 0)).slice(0, 8),
    [products],
  );

  const cartCount = getCartCount();
  const heroImage = featured[0]?.image || newArrivals[0]?.image || '';

  return (
    <div className="min-h-screen bg-white" data-testid="shop-landing-page">
      <Header />

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden pt-28 lg:pt-32" data-testid="shop-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-100 via-white to-white" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-8 px-4 pb-10 pt-8 lg:grid-cols-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-blue-800">Island Gear</p>
            <h1 className="mt-3 text-4xl font-extrabold italic uppercase tracking-tight text-slate-900 sm:text-5xl">
              The St John Gift Shop
            </h1>
            <p className="mt-4 max-w-xl text-slate-600">
              Tees, hats, visors, stickers and island gear &mdash; wear it home, gift it, or send a tip to
              your favourite captain. Everything ships from St John, U.S. Virgin Islands.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/shop/products"
                className="rounded bg-red-600 px-7 py-4 font-bold uppercase tracking-wide text-white shadow-lg transition hover:bg-red-700"
                data-testid="shop-hero-shop-all"
              >
                Shop All Products
              </Link>
              {categories[0] && (
                <Link
                  to={`/shop/category/${categories[0].slug}`}
                  className="rounded border-2 border-blue-700 bg-white px-7 py-4 font-bold uppercase tracking-wide text-blue-800 transition hover:bg-blue-50"
                  data-testid="shop-hero-first-category"
                >
                  {categories[0].name}
                </Link>
              )}
            </div>
            {cartCount > 0 && (
              <div className="mt-7 inline-flex flex-wrap items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3" data-testid="shop-cart-summary">
                <ShoppingCart className="h-5 w-5 text-blue-800" />
                <span className="text-sm font-semibold text-slate-700">
                  {cartCount} item{cartCount === 1 ? '' : 's'} in your cart &middot; {money(getCartTotal())}
                </span>
                <Link to="/checkout" className="rounded bg-blue-800 px-4 py-2 text-xs font-bold uppercase tracking-wide text-white hover:bg-blue-900" data-testid="shop-cart-checkout">
                  Checkout
                </Link>
              </div>
            )}
          </div>

          <div className="relative hidden lg:block">
            {heroImage ? (
              <img src={heroImage} alt="St John gift shop" className="mx-auto aspect-square w-full max-w-md rounded-2xl object-cover shadow-2xl" />
            ) : (
              <div className="mx-auto flex aspect-square w-full max-w-md items-center justify-center rounded-2xl bg-slate-100 text-slate-300">
                <ShoppingBag className="h-20 w-20" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ================= TRUST STRIP ================= */}
      <section className="border-y border-slate-200 bg-slate-50" data-testid="shop-trust-strip">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-4 px-4 py-6 sm:grid-cols-3">
          {[
            { Icon: Truck, title: 'Ships from St John', body: 'Island made and island shipped.' },
            { Icon: RefreshCw, title: 'Easy returns', body: 'Something not right? We will sort it out.' },
            { Icon: ShieldCheck, title: 'Secure checkout', body: 'Your payment details are encrypted.' },
          ].map(({ Icon, title, body }) => (
            <div key={title} className="flex items-start gap-3">
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-blue-800" />
              <div>
                <p className="text-sm font-bold text-slate-900">{title}</p>
                <p className="text-sm text-slate-500">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {loading || !_loaded ? (
        <div className="flex justify-center py-24" data-testid="shop-loading"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>
      ) : !cart_enabled ? (
        <p className="py-24 text-center text-slate-500" data-testid="shop-disabled">Our gift shop is being updated. Please check back soon.</p>
      ) : products.length === 0 ? (
        <div className="py-24 text-center" data-testid="shop-empty">
          <Package className="mx-auto h-12 w-12 text-slate-300" />
          <p className="mt-4 text-slate-500">Products are being added. Check back soon!</p>
        </div>
      ) : (
        <div className={left_menu_enabled ? 'mx-auto flex max-w-7xl gap-8 px-4' : ''}>
          {left_menu_enabled && (
            <aside className="hidden w-64 shrink-0 pt-10 lg:block">
              <div className="sticky top-24"><ShopCategoryMenu /></div>
            </aside>
          )}
          <div className={left_menu_enabled ? 'min-w-0 flex-1' : ''}>
          <Row
            title="Featured"
            subtitle="Hand picked by us"
            products={featured}
            onAddToCart={addToCart}
            testId="shop-featured-row"
            cta={{ label: 'Shop all', href: '/shop/products' }}
          />
          <Row
            title="New Arrivals"
            subtitle="Just landed in the shop"
            products={newArrivals}
            onAddToCart={addToCart}
            testId="shop-new-arrivals-row"
            cta={{ label: 'Shop all', href: '/shop/products' }}
          />
          <Row
            title="Best Sellers"
            subtitle="What everyone is taking home"
            products={bestSellers}
            onAddToCart={addToCart}
            testId="shop-best-sellers-row"
          />

          {/* browse by category - links only, the grid itself stays off this page */}
          {categories.length > 0 && (
            <section className="mx-auto max-w-7xl px-4 pb-16 pt-4" data-testid="shop-category-links">
              <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500">Browse</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {categories.map((c) => (
                  <Link
                    key={c.id}
                    to={`/shop/category/${c.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-blue-300 hover:text-blue-800"
                    data-testid={`shop-category-link-${c.slug}`}
                  >
                    <Sparkles className="h-3.5 w-3.5 text-blue-700" /> {c.name}
                  </Link>
                ))}
              </div>
            </section>
          )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ShopLandingPage;
